/**
 * @design-contract CUR-113 + CUR-206
 *
 * Follow-along scroll pin for an active streaming response. Owns:
 *   1. User-scroll-intent listeners (wheel / touchstart / touchmove /
 *      keydown) wired to the scroll container. The timestamp they update
 *      pauses the pin while the user is mid-scroll.
 *   2. The RAF loop that, while `isLoading` is true AND the user is
 *      already near the bottom, writes `scrollTop` to the bottom every
 *      animation frame.
 *
 * INSTRUCTIONS FOR HUMAN OR AI AGENTS EDITING THIS FILE:
 *   - DO NOT modify the three rules in `tick` to make a failing test
 *     pass somewhere else. Each rule guards a shipped bug:
 *       Rule 1 (distance < BOTTOM_PROXIMITY_THRESHOLD_PX): pin only
 *       when the user is near the bottom. Removing this yanks
 *       readers down mid-stream.
 *       Rule 2 (intentAgeMs >= 250): pause for 250 ms after every
 *       wheel/touch/keydown. Removing this re-pins the user
 *       frame-by-frame; they can never escape.
 *       Rule 3 (only WRITE when not already at target): without this
 *       the onScroll handler fires at 60 Hz during streaming and
 *       thrashes scroll-to-bottom-button visibility + CPU.
 *   - DO NOT widen `BOTTOM_PROXIMITY_THRESHOLD_PX`. Source it from
 *     `chatLayoutTokens` so it agrees with the onScroll handler.
 *   - All listener registrations are passive (where supported); changing
 *     this can delay user input.
 *
 * Guarded by:
 *   - `pins scrollTop to bottom each frame while streaming and user is
 *      near the bottom`
 *   - `pauses the follow-along pin after a user-driven scroll input`
 *   - `does not fight a wheel-up while the user is actively scrolling
 *      during a streaming response`
 */

import { useCallback, useEffect, useRef } from 'react'
import { BOTTOM_PROXIMITY_THRESHOLD_PX } from './chatLayoutTokens'

/** Window (ms) after a user-driven scroll input where the pin pauses. */
export const USER_INTENT_PAUSE_MS = 250

export interface UseTurnScrollPinOptions {
  /** State-stored scroll-container element. Used as the effect input AND
   *  the listener target. Passing in the element (not a ref) lets the
   *  consumer expose it via `setRef` callback to other measurement code. */
  scrollContainer: HTMLDivElement | null
  /** Always-current scroll-container ref. The RAF tick reads this so it
   *  picks up the latest container without re-running the effect. */
  scrollContainerRef: React.MutableRefObject<HTMLDivElement | null>
  /** True while the assistant response is actively streaming. */
  isLoading: boolean
}

export interface UseTurnScrollPinResult {
  /** Ref of the last user-driven scroll-intent timestamp. Other auto-
   *  scroll paths (initial scroll, scroll-to-bottom-button) MUST update
   *  this ref so the pin doesn't fight them. */
  lastUserScrollIntentMsRef: React.MutableRefObject<number>
  /** Callback bound to wheel / touchstart / touchmove / keydown on the
   *  scroll container by the hook. Exposed so consumers can also call
   *  it imperatively (e.g. from a scrollTo) when needed. */
  noteUserScrollIntent: () => void
}

export function useTurnScrollPin({
  scrollContainer,
  scrollContainerRef,
  isLoading
}: UseTurnScrollPinOptions): UseTurnScrollPinResult {
  const lastUserScrollIntentMsRef = useRef(0)

  // ─────────────────────────────────────────────────────────────────────
  // INVARIANT (FRAGILE): `noteUserScrollIntent` MUST be called on every
  // deliberate user-driven scroll input (wheel, touchstart/move, scroll
  // keydown). The follow-along RAF below uses this timestamp to pause
  // its bottom-pinning while the user is mid-scroll. Drop the wheel
  // listener and the user can no longer pull themselves up off the
  // bottom — the RAF re-pins them every frame.
  // ─────────────────────────────────────────────────────────────────────
  const noteUserScrollIntent = useCallback(() => {
    lastUserScrollIntentMsRef.current = Date.now()
  }, [])

  // User-scroll-intent listeners on the scroll container. Registered on
  // the container (not the document) so we only catch input meant for
  // THIS scroll surface. Touchmove is included because long swipes can
  // outlast the user-intent gate window. Passive listeners avoid any
  // chance of delaying user input.
  useEffect(() => {
    const container = scrollContainer
    if (!container) return
    container.addEventListener('wheel', noteUserScrollIntent, { passive: true })
    container.addEventListener('touchstart', noteUserScrollIntent, { passive: true })
    container.addEventListener('touchmove', noteUserScrollIntent, { passive: true })
    container.addEventListener('keydown', noteUserScrollIntent, { passive: true })
    return () => {
      container.removeEventListener('wheel', noteUserScrollIntent)
      container.removeEventListener('touchstart', noteUserScrollIntent)
      container.removeEventListener('touchmove', noteUserScrollIntent)
      container.removeEventListener('keydown', noteUserScrollIntent)
    }
  }, [scrollContainer, noteUserScrollIntent])

  // ─────────────────────────────────────────────────────────────────────
  // INVARIANT (FRAGILE): Follow-along scroll during an active streaming
  // response. While `isLoading` is true and the user is near the bottom
  // (and NOT mid-scroll themselves), pin `scrollTop` to the bottom on
  // every animation frame. This replaces the perceived "jerk" of
  // discrete throttled snaps (the prior post-stream design dropped a
  // 100ms-throttled `scrollIntoView` at the end of streaming, which
  // produced a visible jolt every line break as content grew between
  // snaps) with continuous tracking at frame rate.
  // ─────────────────────────────────────────────────────────────────────
  // Three rules MUST hold simultaneously — see file banner. DO NOT
  // "simplify" by removing the user-intent gate or by widening the
  // proximity threshold. Each reintroduces a shipped bug.
  useEffect(() => {
    if (!isLoading) return
    const container = scrollContainer
    if (!container) return

    let cancelled = false
    let rafHandle: number | null = null

    const tick = () => {
      if (cancelled) return
      const c = scrollContainerRef.current
      if (c) {
        const intentAgeMs = Date.now() - lastUserScrollIntentMsRef.current
        if (intentAgeMs >= USER_INTENT_PAUSE_MS) {
          const distance = c.scrollHeight - c.scrollTop - c.clientHeight
          if (distance > 0 && distance < BOTTOM_PROXIMITY_THRESHOLD_PX) {
            c.scrollTop = c.scrollHeight - c.clientHeight
          }
        }
      }
      rafHandle = requestAnimationFrame(tick)
    }

    rafHandle = requestAnimationFrame(tick)
    return () => {
      cancelled = true
      if (rafHandle !== null) cancelAnimationFrame(rafHandle)
    }
  }, [isLoading, scrollContainer, scrollContainerRef])

  return { lastUserScrollIntentMsRef, noteUserScrollIntent }
}
